import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent as AlertContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle as AlertTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar, Plus, Edit3, Trash2, Clock, MapPin } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEventsPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', date: '', time: '', location: '', show_date: true, show_time: true });
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
      return;
    }
    if (isAdmin) fetchEvents();
  }, [isAdmin, authLoading, navigate]);

  const fetchEvents = async () => {
    try {
      const { data } = await axios.get(`${API}/events`);
      setEvents(data);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingEvent(null);
    setForm({ title: '', description: '', date: '', time: '', location: '', show_date: true, show_time: true });
    setDialogOpen(true);
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setForm({
      title: event.title,
      description: event.description,
      date: event.date,
      time: event.time || '',
      location: event.location || '',
      show_date: event.show_date !== false,
      show_time: event.show_time !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, form);
        toast.success('Event updated!');
      } else {
        await axios.post(`${API}/events`, form);
        toast.success('Event created!');
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleDelete = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Event deleted');
      setDeleteTarget(null);
      fetchEvents();
    } catch (err) {
      toast.error('Failed to delete event');
    }
  };

  const handleImageUpload = async (eventId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/admin/events/${eventId}/image`, fd);
      toast.success('Image uploaded');
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const handleImageRemove = async (eventId) => {
    try {
      await axios.delete(`${API}/admin/events/${eventId}/image`);
      toast.success('Image removed');
      fetchEvents();
    } catch (err) {
      toast.error('Failed to remove image');
    }
  };

  if (authLoading || loading) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="admin-events-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-8 h-8 text-cyan-400" />
              <h1 className="font-heading text-3xl font-bold uppercase text-white">
                Manage Events
              </h1>
            </div>
            <p className="text-zinc-500">Create and manage events shown under Regular Events</p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none"
            data-testid="create-event-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Event
          </Button>
        </div>

        {/* Events List */}
        {events.length === 0 ? (
          <div className="text-center py-24 bg-[#121212] border border-zinc-800" data-testid="no-events">
            <Calendar className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-xl text-white mb-2">No Events</h2>
            <p className="text-zinc-500 mb-6">Create your first event to display on the homepage</p>
            <Button onClick={openCreate} className="bg-cyan-400 text-black hover:bg-cyan-300 rounded-none">
              Create Event
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event) => (
              <div
                key={event.id}
                className="bg-[#121212] border border-white/5 p-6 card-hover"
                data-testid={`event-item-${event.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-5 flex-1 min-w-0">
                    {/* Date badge */}
                    <div className="flex-shrink-0 bg-cyan-400/10 border border-cyan-400/30 px-4 py-3 text-center">
                      {event.show_date !== false && <span className="font-mono text-xs text-cyan-400 block">{event.date}</span>}
                      {event.show_time !== false && event.time && <span className="font-mono text-xs text-zinc-500 block mt-1">{event.time}</span>}
                      {event.show_date === false && event.show_time === false && <span className="font-mono text-xs text-zinc-600 block">No date</span>}
                    </div>
                    {/* Details */}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-heading text-lg font-bold text-white mb-1">{event.title}</h3>
                      <p className="text-zinc-400 text-sm mb-2">{event.description}</p>
                      {event.location && (
                        <div className="flex items-center gap-1 text-zinc-500 text-xs mb-2">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </div>
                      )}
                      {event.image_url && (
                        <div className="mt-2 inline-block relative">
                          <img src={`${BASE}${event.image_url}`} alt={event.title} className="max-h-32 w-auto object-contain border border-zinc-800" data-testid={`event-img-preview-${event.id}`} />
                          <button
                            onClick={() => handleImageRemove(event.id)}
                            className="absolute top-1 right-1 bg-black/70 text-red-400 hover:text-red-300 p-1"
                            title="Remove image"
                            data-testid={`remove-event-img-${event.id}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <label
                      className="border border-zinc-800 text-zinc-400 hover:text-yellow-400 hover:border-yellow-400 rounded-none h-9 px-3 flex items-center cursor-pointer transition-colors"
                      title="Upload image"
                      data-testid={`upload-event-image-${event.id}`}
                    >
                      <ImagePlus className="w-4 h-4 mr-1" />
                      <span className="text-xs">Image</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(event.id, e.target.files?.[0])}
                      />
                    </label>
                    <Button
                      onClick={() => openEdit(event)}
                      variant="outline"
                      size="sm"
                      className="border-zinc-800 text-zinc-400 hover:text-cyan-400 hover:border-cyan-400 rounded-none h-9 px-3"
                      data-testid={`edit-event-${event.id}`}
                    >
                      <Edit3 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => setDeleteTarget(event)}
                      variant="outline"
                      size="sm"
                      className="border-zinc-800 text-zinc-400 hover:text-red-500 hover:border-red-500 rounded-none h-9 px-3"
                      data-testid={`delete-event-${event.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase">
              {editingEvent ? 'Edit Event' : 'Create Event'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Event Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., Weekly Showdown"
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                data-testid="event-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe the event..."
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[80px] focus:border-cyan-400 resize-none"
                data-testid="event-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm uppercase tracking-wide">Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  required={form.show_date}
                  disabled={!form.show_date}
                  className="bg-black border-zinc-800 text-white rounded-none h-12 focus:border-cyan-400 disabled:opacity-40"
                  data-testid="event-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm uppercase tracking-wide">Time</Label>
                <Input
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  required={form.show_time}
                  disabled={!form.show_time}
                  className="bg-black border-zinc-800 text-white rounded-none h-12 focus:border-cyan-400 disabled:opacity-40"
                  data-testid="event-time-input"
                />
              </div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.show_date}
                  onCheckedChange={(checked) => setForm({ ...form, show_date: !!checked })}
                  className="border-zinc-600 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400 rounded-none"
                  data-testid="show-date-checkbox"
                />
                <span className="text-zinc-400 text-sm">Display date</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.show_time}
                  onCheckedChange={(checked) => setForm({ ...form, show_time: !!checked })}
                  className="border-zinc-600 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400 rounded-none"
                  data-testid="show-time-checkbox"
                />
                <span className="text-zinc-400 text-sm">Display time</span>
              </label>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Location (Optional)</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g., Discord Server"
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                data-testid="event-location-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
              data-testid="save-event-button"
            >
              {editingEvent ? 'Update Event' : 'Create Event'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertContent className="bg-[#121212] border-zinc-800 rounded-none">
          <AlertDialogHeader>
            <AlertTitle className="font-heading text-xl text-white uppercase">Delete Event</AlertTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete <span className="text-white font-bold">"{deleteTarget?.title}"</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-none" data-testid="cancel-delete-event">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-red-500 text-white hover:bg-red-600 rounded-none"
              data-testid="confirm-delete-event"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertContent>
      </AlertDialog>
    </main>
  );
}
